import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Award, CalendarClock, Info, Loader2, PenLine, Settings, Type, Undo2, Wand2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { certificationService, type CreateManualCertificationDto } from '@/services/certification.service';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { CERTIFICATION_CREATION_PERMISSION } from '@/constants/constant-permissions';
import { ADMIN_CERTIFICATIONS, ADMIN_CERTIFICATION_DETAILS } from '@/constants/constant-routes';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Toggle } from '@/components/ui/Toggle';
import { TagsField } from '@/components/common/form-fields';

interface ManualCertificationFormData {
  title: string;
  description: string;
  targeted_audience: string;
  time_limit_minutes: string;
  passing_score: string;
  max_attempts: string;
  shuffle_questions: boolean;
  shuffle_options: boolean;
  show_feedback: boolean;
  show_correct_answers: boolean;
  allow_retry: boolean;
  validity_type: 'LIFETIME' | 'FIXED_DURATION';
  validity_months: string;
}

const wholeNumber = (min: number, max?: number, optional = false) => (v: string) => {
  if (!v && optional) return true;
  if (!/^\d+$/.test(String(v ?? ''))) return 'Enter a whole number';
  const n = Number(v);
  if (n < min) return `Must be at least ${min}`;
  if (max !== undefined && n > max) return `Must be at most ${max}`;
  return true;
};

// Manual creation: same exam settings as AI generation, but no content sources and no AI.
// Creates the certification with an empty exam; questions are added on the details page.
export default function CreateManualCertification() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  usePermissionRouteAccess([
    CERTIFICATION_CREATION_PERMISSION.CERTIFICATION_CREATION_READ,
    CERTIFICATION_CREATION_PERMISSION.CERTIFICATION_CREATION_WRITE,
  ]);
  const canWrite = useHasPermission([CERTIFICATION_CREATION_PERMISSION.CERTIFICATION_CREATION_WRITE]);

  const [tags, setTags] = useState<string[]>([]);

  const { control, handleSubmit, watch, setValue, formState: { errors } } = useForm<ManualCertificationFormData>({
    mode: 'all',
    // Same defaults as the AI create form
    defaultValues: {
      title: '',
      description: '',
      targeted_audience: '',
      time_limit_minutes: '60',
      passing_score: '70',
      max_attempts: '3',
      shuffle_questions: true,
      shuffle_options: true,
      show_feedback: true,
      show_correct_answers: true,
      allow_retry: true,
      validity_type: 'LIFETIME',
      validity_months: '12',
    },
  });

  const allowRetry = watch('allow_retry');
  const validityType = watch('validity_type');
  const description = watch('description');

  const [isEnhancing, setIsEnhancing] = useState(false);
  const [descriptionBeforeEnhancement, setDescriptionBeforeEnhancement] = useState<string | null>(null);

  // Same enhancer as AI generation, asked for a learner-facing description rather than an AI brief
  const handleEnhanceDescription = async () => {
    const current = description || '';
    if (current.trim().length < 10) {
      toast.error('Please enter at least 10 characters to enhance');
      return;
    }
    setIsEnhancing(true);
    const loadingToast = toast.loading('Enhancing your description with AI...');
    try {
      const result = await certificationService.enhanceUserInput({
        user_input: current.trim(),
        targeted_audience: (watch('targeted_audience') || '').trim() || undefined,
        purpose: 'DESCRIPTION',
      });
      setDescriptionBeforeEnhancement(current);
      setValue('description', result.enhanced_input, { shouldValidate: true, shouldDirty: true });
      toast.success('Description enhanced', { id: loadingToast });
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error?.error_description ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to enhance description',
        { id: loadingToast }
      );
    } finally {
      setIsEnhancing(false);
    }
  };

  const createMutation = useMutation({
    mutationFn: (data: CreateManualCertificationDto) => certificationService.createManualCertification(data),
    onSuccess: (certification) => {
      toast.success('Certification created. Add questions, then publish it.');
      queryClient.invalidateQueries({ queryKey: ['minimal-certifications'] });
      navigate(`${ADMIN_CERTIFICATION_DETAILS}/${certification._id}`);
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.error?.error_description ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create certification'
      );
    },
  });

  const onSubmit = (data: ManualCertificationFormData) => {
    createMutation.mutate({
      title: data.title.trim(),
      description: data.description.trim(),
      targeted_audience: data.targeted_audience.trim() || undefined,
      tags: tags.length > 0 ? tags : undefined,
      time_limit_minutes: data.time_limit_minutes ? Number(data.time_limit_minutes) : undefined,
      passing_score: Number(data.passing_score),
      shuffle_questions: data.shuffle_questions,
      shuffle_options: data.shuffle_options,
      show_feedback: data.show_feedback,
      show_correct_answers: data.show_correct_answers,
      allow_retry: data.allow_retry,
      // Empty means unlimited attempts
      max_attempts: data.allow_retry && data.max_attempts ? Number(data.max_attempts) : undefined,
      validity_type: data.validity_type,
      validity_months: data.validity_type === 'FIXED_DURATION' ? Number(data.validity_months) : undefined,
    });
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl">
          <PenLine className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Create Certification Manually</h1>
          <p className="text-gray-600">Set up the certification yourself - no AI generation</p>
        </div>
      </div>

      {/* How it works */}
      <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex gap-3">
          <Info className="w-5 h-5 text-yellow-700 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-yellow-900 mb-1">How manual creation works</h3>
            <ul className="text-sm text-yellow-900 space-y-1 ml-4">
              <li>• Enter the certification details and exam settings below</li>
              <li>• After creating it, add your questions on the certification page</li>
              <li>• Publish once it has at least one question, then assign it to learners</li>
            </ul>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Type className="w-5 h-5 text-yellow-600" />
            <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
          </div>
          <div className="space-y-4">
            <Input<ManualCertificationFormData>
              name="title"
              control={control}
              label="Certification Title"
              required
              placeholder="e.g., Workplace Safety Certification"
              error={errors.title?.message}
              rules={{
                validate: (v: string) =>
                  !v?.trim() ? 'Title is required'
                    : v.trim().length > 200 ? 'Title cannot exceed 200 characters'
                    : true,
              }}
            />
            <div className="flex items-center justify-between -mb-3">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-2">
                {/* Undo - shows only after an enhancement */}
                {descriptionBeforeEnhancement !== null && (
                  <button
                    type="button"
                    onClick={() => {
                      setValue('description', descriptionBeforeEnhancement, { shouldValidate: true, shouldDirty: true });
                      setDescriptionBeforeEnhancement(null);
                      toast.success('Reverted to original text');
                    }}
                    className="p-2 rounded-lg transition-all bg-gray-100 hover:bg-gray-200 text-gray-600"
                    title="Undo enhancement"
                  >
                    <Undo2 className="w-4 h-4" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleEnhanceDescription}
                  disabled={!canWrite || isEnhancing || (description || '').trim().length < 10}
                  title={!canWrite ? 'You do not have permission to perform this action' : (description || '').trim().length < 10 ? 'Enter at least 10 characters to enhance' : undefined}
                  className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm font-medium"
                >
                  {isEnhancing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                  {isEnhancing ? 'Enhancing...' : 'Enhance with AI'}
                </button>
              </div>
            </div>
            <Textarea<ManualCertificationFormData>
              name="description"
              control={control}
              rows={6}
              maxLength={1000}
              placeholder="Example: This certification validates practical knowledge of workplace safety, covering fire prevention, first aid, hazard identification and incident reporting. It is designed for all employees who need to demonstrate they can respond correctly to common workplace emergencies."
              error={errors.description?.message}
              helperText={`${description?.length || 0}/1000 characters (minimum 100). Describe what the certification covers and who it is for.`}
              rules={{
                // Same rules as the AI generation description
                validate: (v: string) =>
                  !v?.trim() ? 'Please describe what the certification covers'
                    : v.trim().length < 100 ? 'Description must be at least 100 characters'
                    : v.length > 1000 ? 'Description cannot exceed 1000 characters'
                    : true,
              }}
            />
            <Input<ManualCertificationFormData>
              name="targeted_audience"
              control={control}
              label="Target Audience (Optional)"
              placeholder="e.g., All employees"
              error={errors.targeted_audience?.message}
            />
            <TagsField
              label="Tags"
              description="Add relevant tags to help categorize this certification"
              value={tags}
              onChange={setTags}
              placeholder="Add a tag"
              maxTags={10}
            />
          </div>
        </div>

        {/* Exam Configuration */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Settings className="w-5 h-5 text-yellow-600" />
            <h2 className="text-lg font-semibold text-gray-900">Exam Configuration</h2>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input<ManualCertificationFormData>
                name="time_limit_minutes"
                control={control}
                type="number"
                min={1}
                label="Time Limit (minutes)"
                placeholder="No limit"
                helperText="Leave empty for unlimited time"
                error={errors.time_limit_minutes?.message}
                rules={{ validate: wholeNumber(1, undefined, true) }}
              />
              <Input<ManualCertificationFormData>
                name="passing_score"
                control={control}
                type="number"
                min={50}
                max={100}
                label="Passing Score (%)"
                required
                error={errors.passing_score?.message}
                rules={{ validate: wholeNumber(50, 100) }}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Toggle<ManualCertificationFormData> name="shuffle_questions" control={control} label="Shuffle Questions" description="Randomize question order for each attempt" />
              <Toggle<ManualCertificationFormData> name="shuffle_options" control={control} label="Shuffle Options" description="Randomize answer options for MCQs" />
              <Toggle<ManualCertificationFormData> name="show_feedback" control={control} label="Show Feedback" description="Display explanations after submission" />
              <Toggle<ManualCertificationFormData> name="show_correct_answers" control={control} label="Show Correct Answers" description="Reveal correct answers after submission" />
              <Toggle<ManualCertificationFormData> name="allow_retry" control={control} label="Allow Retry" description="Learners can retake the exam if they don't pass" />
            </div>
            {allowRetry && (
              <div className="max-w-sm">
                <Input<ManualCertificationFormData>
                  name="max_attempts"
                  control={control}
                  type="number"
                  min={1}
                  label="Maximum Attempts"
                  placeholder="Unlimited"
                  helperText="Leave empty to allow unlimited attempts"
                  error={errors.max_attempts?.message}
                  rules={{ validate: wholeNumber(1, undefined, true) }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Certificate Validity */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <CalendarClock className="w-5 h-5 text-yellow-600" />
            <h2 className="text-lg font-semibold text-gray-900">Certificate Validity</h2>
          </div>
          <div className="space-y-3">
            <Controller
              name="validity_type"
              control={control}
              render={({ field }) => (
                <div className="flex flex-wrap gap-2">
                  {([
                    { value: 'LIFETIME', label: 'Lifetime' },
                    { value: 'FIXED_DURATION', label: 'Fixed duration' },
                  ] as const).map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => field.onChange(option.value)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${field.value === option.value
                        ? 'bg-yellow-50 border-yellow-500 text-yellow-800'
                        : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            />
            {validityType === 'FIXED_DURATION' ? (
              <div className="max-w-xs">
                <Input<ManualCertificationFormData>
                  name="validity_months"
                  control={control}
                  type="number"
                  min={1}
                  max={120}
                  label="Validity (months)"
                  required
                  helperText="Certificates expire this many months after issue"
                  error={errors.validity_months?.message}
                  rules={{ validate: (v: string) => validityType !== 'FIXED_DURATION' || wholeNumber(1, 120)(v) }}
                />
              </div>
            ) : (
              <p className="text-xs text-gray-500">Certificates never expire.</p>
            )}
          </div>
        </div>

        <div className="flex justify-end items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(ADMIN_CERTIFICATIONS)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canWrite || createMutation.isPending}
            title={!canWrite ? 'You do not have permission to perform this action' : undefined}
            className="flex items-center gap-2 px-6 py-2 text-sm font-semibold text-white bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg hover:from-yellow-600 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
          >
            {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Award className="w-4 h-4" />}
            <span>{createMutation.isPending ? 'Creating Certification...' : 'Create Certification'}</span>
          </button>
        </div>
      </form>
    </div>
  );
}
